import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { checkUserFranchiseeStatus } from '../services/signupService';
import { getUserInfo } from '../services/userService';
import { useAdminView } from '../context/AdminViewContext';
import { SuspensionProvider } from '../context/SuspensionContext';
import SuspendedBanner from './SuspendedBanner';

const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hasCompleteAccount, setHasCompleteAccount] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const { isAdminView, adminSelectedFseId } = useAdminView();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const idToken = await currentUser.getIdToken();
          const userInfo = await getUserInfo(idToken);
          setPasswordResetRequired(!!userInfo.passwordResetRequired);
          setIsSuspended(!!userInfo.isSuspended);

          if (userInfo.userType === 'admin') {
            setIsAdminUser(true);
            if (isAdminView && adminSelectedFseId) {
              setHasCompleteAccount(true);
            } else {
              setHasCompleteAccount(false);
            }
          } else {
            setIsAdminUser(false);
            const statusResult = await checkUserFranchiseeStatus(currentUser.uid);
            setHasCompleteAccount(statusResult.hasCompleteAccount);

            if (!statusResult.hasCompleteAccount) {
              console.log('User has incomplete account, will redirect to login');
              await auth.signOut();
              setUser(null);
              setHasCompleteAccount(false);
            }
          }
        } catch (error) {
          console.error('Error checking user status in ProtectedRoute:', error);

          setHasCompleteAccount(false);
          setPasswordResetRequired(false);
          setIsSuspended(false);
        }
      } else {
        setIsAdminUser(false);
        setHasCompleteAccount(false);
        setPasswordResetRequired(false);
        setIsSuspended(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAdminView, adminSelectedFseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Force a password change while the temp-password flag is set (allow the change-password page itself through).
  if (user && passwordResetRequired && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // /change-password skips the current-password check, so it's only for forced temp-password resets.
  // A logged-in user without the flag must use the Account modal (which reauthenticates) instead.
  if (user && !passwordResetRequired && location.pathname === '/change-password') {
    return <Navigate to="/" replace />;
  }

  if (isAdminUser && isAdminView && adminSelectedFseId) {
    // The admin's own session is never suspended - suspension is a franchisee account state,
    // and impersonation must mirror what the backend gate sees for the admin's own token.
    return (
      <SuspensionProvider isSuspended={false}>
        {children}
      </SuspensionProvider>
    );
  }

  if (user && hasCompleteAccount) {
    return (
      <SuspensionProvider isSuspended={isSuspended}>
        {isSuspended && <SuspendedBanner />}
        {children}
      </SuspensionProvider>
    );
  }

  return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} />;
};

export default ProtectedRoute;
